// Storage tab — what the install's files occupy (`GET /api/v1/settings/
// storage`): the current versions, every kept version, the trash, and where
// the bytes go (by extension, the largest files). Read-only; the numbers are
// one grouped query each and the trash line is what a purge would free.

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Database, HardDrive, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import moment from 'moment';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api, ep } from '../lib/api.js';
import { formatBytes } from '../lib/format.js';

function Stat({ icon, label, value, hint }) {
  const Icon = icon;
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
        <Icon className="h-4 w-4" /> {label}
      </div>
      <div className="mt-1 text-2xl font-semibold text-foreground">{value}</div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

export const StorageTab = () => {
  const { t } = useTranslation('console');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.get(ep.storage());
    setLoading(false);
    if (res.success) setReport(res.response);
    else toast.error(res.message);
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading && !report) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-blue-500 dark:text-blue-400 animate-spin" /></div>;
  }
  if (!report) return null;

  const maxExt = Math.max(1, ...report.by_extension.map((r) => r.bytes));

  return (
    <div className="space-y-6" data-testid="storage-report">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('storage.intro')}</p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {t('indexing.updatedAt', { time: moment(report.generated_at).format('HH:mm:ss') })}
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} aria-label={t('storage.refresh')}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Stat icon={HardDrive} label={t('storage.current')} value={formatBytes(report.bytes_current)} hint={t('storage.filesCount', { count: report.files })} />
        <Stat icon={Database} label={t('storage.allVersions')} value={formatBytes(report.bytes_all_versions)} hint={t('storage.versionsCount', { count: report.versions })} />
        <Stat icon={Trash2} label={t('storage.trash')} value={formatBytes(report.bytes_trash)} hint={t('storage.filesTrashed', { count: report.files_trashed })} />
        <Stat icon={Database} label={t('storage.history')} value={formatBytes(Math.max(0, report.bytes_all_versions - report.bytes_current))} hint={t('storage.historyHint')} />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <section>
          <h3 className="mb-2 text-sm font-medium text-foreground">{t('storage.byExtension')}</h3>
          {report.by_extension.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('storage.nothingYet')}</p>
          ) : (
            <ul className="space-y-1.5">
              {report.by_extension.map((row) => (
                <li key={row.key} className="text-sm">
                  <div className="flex items-center justify-between">
                    <span className="font-mono">.{row.key || '?'}</span>
                    <span className="text-muted-foreground">{formatBytes(row.bytes)} · {row.count}</span>
                  </div>
                  <div className="mt-0.5 h-1.5 w-full rounded bg-muted">
                    <div className="h-1.5 rounded bg-blue-400" style={{ width: `${Math.max(2, (row.bytes / maxExt) * 100)}%` }} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section>
          <h3 className="mb-2 text-sm font-medium text-foreground">{t('storage.largest')}</h3>
          {report.largest.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('storage.nothingYet')}</p>
          ) : (
            <div className="rounded-lg border bg-card overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('storage.table.name')}</TableHead>
                    <TableHead className="w-28 text-right">{t('storage.table.size')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.largest.map((row) => (
                    <TableRow key={row.file_uuid}>
                      <TableCell className="max-w-64 truncate" title={row.name || ''}>
                        {row.name || String(row.file_uuid).slice(0, 8)}
                        {row.trashed && <span className="ml-2 rounded bg-muted px-1.5 text-xs text-muted-foreground">{t('storage.trashed')}</span>}
                      </TableCell>
                      <TableCell className="text-right font-mono text-xs">{formatBytes(row.bytes)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};
